import '../../styles/styles.css';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

import Layout from '../../components/Layout';

import AnimateIn from '../../components/AnimateIn';
import VimeoVideo from '../../components/VimeoVideo';
import Image from '../../components/Image';
import ColumnLists from '../../components/ColumnLists';
import LinkButton from '../../components/LinkButton';
import LinkList from '../../components/LinkList';
import Quote from '../../components/Quote';
import WorkHeader from '../../components/WorkHeader';

import { getProject } from '../../config/general';
import hardcodedStaticProps from '../../lib/harcodedStaticProps';

const SamsungCL = ({ featuredProjectsList }) => {
  const project = getProject('samsung-cl');

  return (
    <Layout
      metaData={{ data: project }}
      featuredProjectsList={featuredProjectsList}
    >
      <article className="components page">
        <header className="g-row mb-100 component component--visible">
          <p className="mb-200">Smart connections for easy living.</p>
          <h1 className="t-mega project-title">Samsung</h1>
          <p className="c-purple">
            Connected Living <span className="u-dot-del">·</span>{' '}
            <span className="f-italic">
              Content <span className="u-dot-del">·</span> Installation
            </span>
          </p>
        </header>

        <VimeoVideo
          component={{
            video: '315877887',
            posterVideo: '315877887',
          }}
        />

        <AnimateIn className="text g-row">
          <p>
            Together with their retail partners, Samsung continuously works to
            educate consumers on the benefits of connected products.
          </p>
        </AnimateIn>

        <VimeoVideo
          component={{
            posterVideo: '328653035',
            calculateRatio: 'auto',
          }}
        />

        <AnimateIn className="text g-row">
          <h2 id="testing-new-directions">Testing new directions</h2>
          <p>
            Despite the rapid growth in the smart home market, a large portion
            of consumers still finds that "smart homes" is something difficult
            to get started with.
          </p>
          <p>
            So as a part of a discovery project to validate different types of
            communication to consumers, Your Majesty was invited to create an
            interactive kiosk that could be tested in retail locations across
            the U.S.
          </p>
        </AnimateIn>

        <Image
          component={{
            image: '//images.yourmajesty.co/20190405095233/Sea-products.png',
            calculateRatio: '1920,3047',
            caption:
              'The goal of the project was to link together four product categories; Home Entertainment, Home Appliances, Smart Home, and Mobile Computing, into one coherent and delightful experience.',
          }}
        />

        <AnimateIn className="text g-row">
          <h2 id="easy-as-1-2-3-">Easy as 1..2..3..!</h2>
          <p>
            The solution was a retail experience that uses a mix of product
            content and illustrated videos to guide retail customers in three
            key acts.
          </p>
        </AnimateIn>

        <Image
          component={{
            image:
              '//images.yourmajesty.co/20190405095356/190122_Samsung_eod.jpg',
            caption:
              'The kiosk features a 16" touchscreen with a connected 55" QLED 4K TV. The unit was designed to be both freestanding or placed as an endcap in an aisle.',
          }}
        />

        <VimeoVideo
          component={{
            posterVideo: '328653779',
            caption:
              '1. Attract — By using eye-catching and bold statements the fixture commands attention in a busy store environment.',
          }}
        />

        <Image
          component={{
            image: '//images.yourmajesty.co/20190405100123/Sea-sketches.png',
            calculateRatio: '1920,1620',
            caption:
              '2. Engage — Derived from the Samsung brand guidelines, we created a family of illustrated characters that star in eight animated shorts. Each short tells a story about how connected products can ease everyday life.',
          }}
        />

        <VimeoVideo
          component={{
            video: '315879449',
            posterVideo: '315879449',
          }}
        />

        <VimeoVideo
          className="g-row"
          component={{
            posterVideo: '329335272',
            calculateRatio: '1596,1080',
            caption:
              '3. Direct — On the Android-based touch screen, consumers can select a product category to explore, and trigger the corresponding animated shorts on the big screen. Upon video completion, the consumer is directed to continue exploring or to contact a sales representative for getting further guidance.',
          }}
        />

        <AnimateIn className="text g-row">
          <h2 id="rolled-out-to-mine-insights">Rolled out to mine insights</h2>
          <p>
            The kiosks has been installed in six retail locations across North
            America and will gather engagement metrics to inform Samsung’s next
            steps in their communication strategy.
          </p>

          <h2 id="curious-to-know-more-">Curious to know more?</h2>
          <p>
            Learn more about our expertise in retail innovation, by sending us a
            note to{' '}
            <a href="mailto: hello@yourmajesty.co">hello@yourmajesty.co</a>, or
            check out our case-studies for{' '}
            <Link href="/work/samsung">
              <a target="_blank" rel="noopener">
                Samsung SmartThings
              </a>
            </Link>{' '}
            or{' '}
            <Link href="/work/adidas">
              <a target="_blank" rel="noopener">
                adidas
              </a>
            </Link>
            .
          </p>

          <h2 id="collaborators">Collaborators</h2>
          <p>
            <a
              href="http://www.steffiepadmos.com"
              target="_blank"
              rel="noopener"
            >
              Steffie Padmos
            </a>
            <br />
            <a
              href="http://cargocollective.com/40redapples"
              target="_blank"
              rel="noopener"
            >
              David Schagerström
            </a>
            <br />
            <a
              href="http://www.tiffanydavenport.com"
              target="_blank"
              rel="noopener"
            >
              Tiffany Davenport
            </a>
            <br />
            <a href="http://www.studio-takt.com" target="_blank" rel="noopener">
              Studio Takt
            </a>
          </p>
        </AnimateIn>

        <ColumnLists
          className="g-row"
          component={{
            title: 'Services',
            columns: [
              {
                title: 'Strategy',
                items: [
                  'Communications Strategy',
                  'Content Strategy',
                  'Creative Strategy',
                  'Experience Mapping',
                  'Prototyping & Validation',
                  'Technical Strategy',
                  'UX Strategy',
                  'Workshop & Facilitation',
                ],
              },

              {
                title: 'Design',
                items: [
                  'Art Direction',
                  'Creative Direction',
                  'Illustration',
                  'Motion Design',
                  'Sound Design',
                  'UI/UX Design',
                  'Visual Design',
                ],
              },

              {
                title: 'Technology',
                items: [
                  'Back-end Development',
                  'Front-end Development',
                  'Functional Prototyping',
                ],
              },

              {
                title: 'Delivery',
                items: [
                  'Analytics & Insights',
                  'Integration Support',
                  'Launch',
                  'Monitoring',
                  'Quality Assurance',
                  'Staff Training & Support',
                ],
              },

              {
                title: 'Content',
                items: ['Animation', 'Copywriting', 'Post Production'],
              },

              {
                title: 'Experiential',
                items: [
                  'Hardware Specification',
                  'Installation Prototyping',
                  'Interactive Environment',
                ],
              },
            ],
          }}
        />
      </article>
    </Layout>
  );
};

export { hardcodedStaticProps as getStaticProps };

export default SamsungCL;
